import { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Card from "@mui/material/Card";
import Alert from "@mui/material/Alert";
import ArgonBox from "components/ArgonBox";
import ArgonTypography from "components/ArgonTypography";
import ArgonInput from "components/ArgonInput";
import ArgonButton from "components/ArgonButton";
import CoverLayout from "layouts/authentication/components/CoverLayout";
import { validateInviteToken, acceptInvite } from "services/userApi";

const bgImage =
  "https://raw.githubusercontent.com/creativetimofficial/public-assets/master/argon-dashboard-pro/assets/img/signin-basic.jpg";

function AcceptInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
    firstName: "",
    lastName: "",
    phone: ""
  });
  
  const [inviteData, setInviteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    // Validate invite token
    validateInvite();
  }, [token]);

  const validateInvite = async () => {
    try {
      setLoading(true);
      const data = await validateInviteToken(token);
      setInviteData(data);
    } catch (err) {
      setError("Invalid or expired invitation link");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    
    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }
    
    // Validate password strength
    if (formData.password.length < 8) {
      setError("Password must be at least 8 characters long");
      return;
    }
    
    try {
      await acceptInvite({
        token,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        phone: formData.phone
      });
      
      setSuccess("Account created successfully! Redirecting to login...");
      
      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/authentication/sign-in");
      }, 2000);
    } catch (err) {
      setError("Failed to create account. Please try again.");
    }
  };

  const isExpired = inviteData && new Date(inviteData.expiresAt) < new Date();

  return (
    <CoverLayout
      title="Welcome to PRM"
      description="Complete your account setup to get started"
      image={bgImage}
    >
      <Card>
        <ArgonBox p={3} mb={1} textAlign="center">
          <ArgonTypography variant="h5" fontWeight="medium">
            Accept Invitation
          </ArgonTypography>
        </ArgonBox>
        
        <ArgonBox pb={3} px={3}>
          {loading ? (
            <ArgonBox textAlign="center" py={5}>
              <ArgonTypography variant="body2" color="text">
                Validating invitation...
              </ArgonTypography>
            </ArgonBox>
          ) : error && !inviteData ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          ) : isExpired ? (
            <Alert severity="error" sx={{ mb: 3 }}>
              This invitation link has expired. Please contact your administrator for a new invitation.
            </Alert>
          ) : (
            <>
              {inviteData && (
                <ArgonBox mb={3}>
                  <Alert severity="info">
                    <ArgonTypography variant="body2" fontWeight="medium">
                      You&apos;ve been invited to join {inviteData.organization}
                    </ArgonTypography>
                    <ArgonTypography variant="caption" display="block">
                      Email: {inviteData.email}
                    </ArgonTypography>
                    <ArgonTypography variant="caption" display="block">
                      Role: {inviteData.role}
                    </ArgonTypography>
                  </Alert>
                </ArgonBox>
              )}
              
              {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
              
              <ArgonBox component="form" role="form" onSubmit={handleSubmit}>
                <ArgonBox display="flex" gap={2} mb={2}>
                  <ArgonBox flex={1}>
                    <ArgonInput
                      name="firstName"
                      placeholder="First Name"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      required
                    />
                  </ArgonBox>
                  <ArgonBox flex={1}>
                    <ArgonInput
                      name="lastName"
                      placeholder="Last Name"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      required
                    />
                  </ArgonBox>
                </ArgonBox>
                
                <ArgonBox mb={2}>
                  <ArgonInput
                    type="email"
                    value={inviteData?.email || ""}
                    disabled
                  />
                </ArgonBox>
                
                <ArgonBox mb={2}>
                  <ArgonInput
                    name="phone"
                    type="tel"
                    placeholder="Phone Number (Optional)"
                    value={formData.phone}
                    onChange={handleInputChange}
                  />
                </ArgonBox>
                
                <ArgonBox mb={2}>
                  <ArgonInput
                    name="password"
                    type="password"
                    placeholder="Password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                  />
                </ArgonBox>
                
                <ArgonBox mb={3}>
                  <ArgonInput
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm Password"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    required
                  />
                </ArgonBox>
                
                <ArgonTypography variant="caption" color="text" mb={2} display="block">
                  Password must be at least 8 characters long
                </ArgonTypography>
                
                <ArgonBox mt={4} mb={1}>
                  <ArgonButton
                    variant="gradient"
                    color="primary"
                    fullWidth
                    type="submit"
                    disabled={!inviteData || isExpired || success}
                  >
                    Create Account
                  </ArgonButton>
                </ArgonBox>
                
                <ArgonBox mt={3} textAlign="center">
                  <ArgonTypography variant="button" color="text" fontWeight="regular">
                    Already have an account?{" "}
                    <ArgonTypography
                      component={Link}
                      to="/authentication/sign-in"
                      variant="button"
                      color="primary"
                      fontWeight="medium"
                    >
                      Sign in
                    </ArgonTypography>
                  </ArgonTypography>
                </ArgonBox>
              </ArgonBox>
            </>
          )}
        </ArgonBox>
      </Card>
    </CoverLayout>
  );
}

export default AcceptInvite;